import { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '@/App';
import GalleryPicker from '@/components/GalleryPicker';
import { 
  Link2, QrCode, Copy, Check, Download, ExternalLink, Plus, Trash2, 
  Eye, Save, Image, Palette, X, Globe, Headphones, 
  BookOpen, Youtube, ShoppingBag, Smartphone, Music, Upload, User,
  Mail, Phone, ChevronUp, ChevronDown, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Opciones de iconos para enlaces
const ICON_OPTIONS = [
  { value: 'link', label: 'Enlace', icon: <Globe className="w-4 h-4" /> },
  { value: 'audiolibro', label: 'Audiolibro', icon: <Headphones className="w-4 h-4" /> },
  { value: 'podcast', label: 'Podcast', icon: <Headphones className="w-4 h-4" /> },
  { value: 'ebook', label: 'E-book', icon: <BookOpen className="w-4 h-4" /> },
  { value: 'amazon', label: 'Amazon', icon: <ShoppingBag className="w-4 h-4" /> },
  { value: 'youtube', label: 'YouTube', icon: <Youtube className="w-4 h-4" /> },
  { value: 'curso', label: 'Curso', icon: <BookOpen className="w-4 h-4" /> },
  { value: 'tienda', label: 'Tienda', icon: <ShoppingBag className="w-4 h-4" /> },
  { value: 'web', label: 'Sitio Web', icon: <Globe className="w-4 h-4" /> },
  { value: 'spotify', label: 'Spotify', icon: <Music className="w-4 h-4" /> },
  { value: 'apple', label: 'Apple', icon: <Smartphone className="w-4 h-4" /> },
  { value: 'viubux', label: 'Viubux', icon: <Headphones className="w-4 h-4" /> },
];

// Degradados predefinidos
const GRADIENT_PRESETS = [
  { name: 'Viubux', value: 'linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%)' },
  { name: 'Océano', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  { name: 'Atardecer', value: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
  { name: 'Aurora', value: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
  { name: 'Bosque', value: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' },
  { name: 'Fuego', value: 'linear-gradient(135deg, #f12711 0%, #f5af19 100%)' },
  { name: 'Noche', value: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)' },
  { name: 'Rosa', value: 'linear-gradient(135deg, #ee9ca7 0%, #ffdde1 100%)' },
  { name: 'Carbón', value: 'linear-gradient(135deg, #232526 0%, #414345 100%)' },
  { name: 'Cielo', value: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)' },
];

// Colores sólidos
const COLOR_PRESETS = [
  '#7c3aed', '#6366f1', '#3b82f6', '#06b6d4', '#10b981',
  '#f59e0b', '#ef4444', '#ec4899', '#8b5cf6', '#1f2937',
];

const LinkPageEditor = ({ user, token, onUpdate }) => {
  const { refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [bgType, setBgType] = useState('gradient'); // 'gradient', 'solid', 'image'
  const [profileData, setProfileData] = useState(null); // Store profile data for sync
  const [showSyncBanner, setShowSyncBanner] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [galleryPickerField, setGalleryPickerField] = useState(null);
  const qrRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    link_page_bio: '',
    link_page_book_cover: '',
    link_page_bg_color: 'linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%)',
    link_page_banner: '',
    link_page_wallpaper: '',
    link_page_wallpaper_blur: 12,
    link_page_links: [],
    social_links: {
      facebook: '',
      instagram: '',
      twitter: '',
      tiktok: '',
      youtube: '',
      linkedin: '',
      spotify: ''
    },
    website: '',
    email_public: '',
    phone: '',
    show_email_public: false,
    show_phone_public: false
  });

  const profileUrl = `${window.location.origin}/${user?.username || ''}`;

  useEffect(() => {
    fetchLinkPageData();
  }, [token]);

  const fetchLinkPageData = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      // Fetch both link page data and base profile data in parallel
      const [linkPageRes, profileRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/user/link-page`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/user/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      const linkPageData = linkPageRes.data;
      const profileData = profileRes.data;
      
      // Store profile data for later sync
      setProfileData(profileData);
      
      // Check if we need to show sync banner (profile has data but link page doesn't)
      const profileHasBio = profileData.bio && profileData.bio.trim() !== '';
      const linkPageHasBio = linkPageData.link_page_bio && linkPageData.link_page_bio.trim() !== '';
      const shouldShowSync = profileHasBio && !linkPageHasBio;
      setShowSyncBanner(shouldShowSync);
      
      // Detectar tipo de fondo
      const bgColor = linkPageData.link_page_bg_color || 'linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%)';
      if (linkPageData.link_page_wallpaper) {
        setBgType('image');
      } else if (bgColor.includes('gradient')) {
        setBgType('gradient');
      } else {
        setBgType('solid');
      }
      
      // Map profile social links to link page format
      const profileSocialLinks = {
        facebook: profileData.facebook || '',
        instagram: profileData.instagram || '',
        twitter: '',
        tiktok: profileData.tiktok || '',
        youtube: '',
        linkedin: profileData.linkedin || '',
        spotify: ''
      };
      
      // Merge data with fallback: if link_page fields are empty, use profile data
      const mergedSocialLinks = {
        ...profileSocialLinks,
        ...(linkPageData.social_links || {})
      };
      
      // Only use profile social links if link_page social_links are all empty
      const hasLinkPageSocials = linkPageData.social_links && 
        Object.values(linkPageData.social_links).some(v => v && v.trim() !== '');
      
      setFormData(prev => ({
        ...prev,
        ...linkPageData,
        // If link_page_bio is empty, use profile bio as default
        link_page_bio: linkPageData.link_page_bio || profileData.bio || '',
        // If link_page_book_cover is empty and profile has a work with cover, suggest using it
        link_page_book_cover: linkPageData.link_page_book_cover || 
          (profileData.works && profileData.works.length > 0 ? profileData.works[0].cover_image : '') || '',
        // Merge social links - prefer link_page data if it exists
        social_links: hasLinkPageSocials 
          ? { ...prev.social_links, ...(linkPageData.social_links || {}) }
          : { ...prev.social_links, ...mergedSocialLinks },
        // Pre-fill email and phone from user object if available
        email_public: linkPageData.email_public || user?.email || '',
        phone: linkPageData.phone || profileData.phone || ''
      }));
    } catch (error) {
      console.error('Error fetching link page data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!token) {
      toast.error('No hay sesión activa');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/user/link-page`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Página de enlaces guardada correctamente');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const addLink = () => {
    if (formData.link_page_links.length >= 15) {
      toast.error('Máximo 15 enlaces permitidos');
      return;
    }
    const newLink = {
      id: `link_${Date.now()}`,
      title: '',
      url: '',
      icon: 'link',
      image: '',
      order: formData.link_page_links.length,
      enabled: true
    };
    setFormData(prev => ({
      ...prev,
      link_page_links: [...prev.link_page_links, newLink]
    }));
  };

  const updateLink = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      link_page_links: prev.link_page_links.map((link, i) => 
        i === index ? { ...link, [field]: value } : link
      )
    }));
  };

  const removeLink = (index) => {
    setFormData(prev => ({
      ...prev,
      link_page_links: prev.link_page_links.filter((_, i) => i !== index)
    }));
  };

  const moveLink = (index, direction) => {
    const newLinks = [...formData.link_page_links];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newLinks.length) return;
    [newLinks[index], newLinks[newIndex]] = [newLinks[newIndex], newLinks[index]];
    newLinks.forEach((link, i) => link.order = i);
    setFormData(prev => ({ ...prev, link_page_links: newLinks }));
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(profileUrl);
    setCopied(true);
    toast.success('Enlace copiado');
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQR = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new window.Image();
    img.onload = () => {
      canvas.width = 400;
      canvas.height = 400;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, 400, 400);
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `viubux-qr-${user?.username || 'code'}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      toast.success('QR descargado');
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  // Sube una imagen a Viubux ImgTool y guarda la URL permanente en el campo indicado
  const uploadImageToImgTool = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 8MB');
      return;
    }
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      toast.error('Usa JPG, PNG, GIF o WEBP');
      return;
    }
    setUploadingField(field);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post(`${BACKEND_URL}/api/imgtool/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const url = res.data?.url;
      if (url) {
        setFormData(prev => ({ ...prev, [field]: url }));
        toast.success('Imagen subida a ImgTool');
      } else {
        toast.error('No se recibió una URL de ImgTool');
      }
    } catch (err) {
      console.error('ImgTool upload error', err);
      toast.error(err.response?.data?.detail || 'Error al subir la imagen a ImgTool');
    } finally {
      setUploadingField(null);
      e.target.value = '';
    }
  };

  // Sube la foto de perfil a ImgTool (endpoint que actualiza el usuario)
  const uploadProfilePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La foto debe ser menor a 5MB');
      return;
    }
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      toast.error('Usa JPG, PNG, GIF o WEBP');
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const res = await axios.post(`${BACKEND_URL}/api/user/upload-photo`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const url = res.data?.photo_url;
      if (url) {
        setPhotoPreview(url);
        toast.success('Foto de perfil actualizada');
        if (refreshUser) refreshUser();
        else if (onUpdate) onUpdate();
      }
    } catch (err) {
      console.error('Profile photo upload error', err);
      toast.error(err.response?.data?.detail || 'Error al subir la foto de perfil');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 5MB');
      return;
    }
    
    // Comprimir imagen si es mayor a 1MB
    if (file.size > 1 * 1024 * 1024) {
      try {
        const compressedBase64 = await compressImage(file, 0.8, 1920);
        setFormData(prev => ({ ...prev, [field]: compressedBase64 }));
        return;
      } catch (error) {
        console.error('Error compressing image:', error);
        // Fallback to original if compression fails
      }
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, [field]: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  // Función para comprimir imágenes
  const compressImage = (file, quality = 0.8, maxWidth = 1920) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Redimensionar si es muy grande
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convertir a base64 con compresión
          const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedBase64);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const getBackgroundStyle = () => {
    if (bgType === 'image' && formData.link_page_wallpaper) {
      return { backgroundColor: '#0f0c29' };
    }
    if (formData.link_page_bg_color?.includes('gradient')) {
      return { background: formData.link_page_bg_color };
    }
    return { backgroundColor: formData.link_page_bg_color || '#7c3aed' };
  };

  // Function to sync profile data to link page fields
  const syncFromProfile = () => {
    if (!profileData) return;
    
    const profileSocialLinks = {
      facebook: profileData.facebook || '',
      instagram: profileData.instagram || '',
      tiktok: profileData.tiktok || '',
      linkedin: profileData.linkedin || ''
    };
    
    setFormData(prev => ({
      ...prev,
      link_page_bio: profileData.bio || prev.link_page_bio,
      link_page_book_cover: (profileData.works && profileData.works.length > 0) 
        ? profileData.works[0].cover_image 
        : prev.link_page_book_cover,
      social_links: {
        ...prev.social_links,
        ...profileSocialLinks
      },
      phone: profileData.phone || prev.phone
    }));
    
    setShowSyncBanner(false);
    toast.success('Datos sincronizados desde tu perfil');
  };

  // Function to sync link page data back to base profile (reverse sync)
  const [syncingToProfile, setSyncingToProfile] = useState(false);
  
  const syncToProfile = async () => {
    if (!token) return;
    
    setSyncingToProfile(true);
    try {
      // Build update payload from link page data
      const updatePayload = {};
      
      // Sync bio if link_page_bio has content
      if (formData.link_page_bio && formData.link_page_bio.trim() !== '') {
        updatePayload.bio = formData.link_page_bio;
      }
      
      // Sync phone if available
      if (formData.phone && formData.phone.trim() !== '') {
        updatePayload.phone = formData.phone;
      }
      
      // Sync social links
      if (formData.social_links) {
        if (formData.social_links.instagram) updatePayload.instagram = formData.social_links.instagram;
        if (formData.social_links.facebook) updatePayload.facebook = formData.social_links.facebook;
        if (formData.social_links.tiktok) updatePayload.tiktok = formData.social_links.tiktok;
        if (formData.social_links.linkedin) updatePayload.linkedin = formData.social_links.linkedin;
      }
      
      // Only proceed if there's something to update
      if (Object.keys(updatePayload).length === 0) {
        toast.info('No hay datos nuevos para sincronizar al perfil');
        return;
      }
      
      await axios.put(`${BACKEND_URL}/api/user/profile`, updatePayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local profileData state to reflect changes
      setProfileData(prev => ({
        ...prev,
        ...updatePayload
      }));
      
      toast.success('Tu perfil base ha sido actualizado con los datos de tu Enlace Único');
    } catch (error) {
      console.error('Error syncing to profile:', error);
      toast.error('Error al sincronizar con tu perfil');
    } finally {
      setSyncingToProfile(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin w-8 h-8 border-4 border-violet-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con URL */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-xl font-bold mb-1">Tu Enlace Único</h2>
            <p className="text-white/70 text-sm">Crea tu página de enlaces personalizada</p>
          </div>
          <Button
            onClick={() => window.open(profileUrl, '_blank')}
            variant="outline"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Ver página
          </Button>
        </div>
        <div className="mt-4 bg-white/10 rounded-xl p-3 flex items-center gap-2">
          <code className="flex-1 text-white/90 text-sm truncate">{profileUrl}</code>
          <Button onClick={copyToClipboard} variant="ghost" size="sm" className="text-white hover:bg-white/20">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Banner de sincronización con perfil */}
      {showSyncBanner && profileData && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-blue-900">Sincronizar con Mi Perfil</p>
              <p className="text-sm text-blue-700">Tu perfil tiene información que puedes importar aquí automáticamente.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => setShowSyncBanner(false)} 
              variant="ghost" 
              size="sm"
              className="text-blue-700 hover:bg-blue-100"
            >
              Ignorar
            </Button>
            <Button 
              onClick={syncFromProfile} 
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Sincronizar
            </Button>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* EDITOR PANEL */}
        <div className="space-y-6">
          
          {/* Datos del Perfil (solo lectura) */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <User className="w-5 h-5 text-violet-600" />
                Datos de tu Perfil
              </h3>
              {profileData && (
                <div className="flex gap-2">
                  <Button 
                    onClick={syncFromProfile} 
                    variant="outline" 
                    size="sm"
                    className="text-violet-600 border-violet-200 hover:bg-violet-50"
                    title="Importar datos desde Mi Perfil"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Importar
                  </Button>
                  <Button 
                    onClick={syncToProfile}
                    disabled={syncingToProfile}
                    variant="outline" 
                    size="sm"
                    className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                    title="Actualizar Mi Perfil con estos datos"
                  >
                    {syncingToProfile ? (
                      <div className="w-3 h-3 mr-1 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Upload className="w-3 h-3 mr-1" />
                    )}
                    Exportar
                  </Button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="relative">
                {(photoPreview || user?.profile_photo) ? (
                  <img src={photoPreview || user.profile_photo} alt="" className="w-16 h-16 rounded-full object-cover" data-testid="linkpage-profile-photo" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-violet-100 flex items-center justify-center">
                    <User className="w-8 h-8 text-violet-600" />
                  </div>
                )}
                <label className="absolute -bottom-1 -right-1 bg-violet-600 hover:bg-violet-700 text-white rounded-full p-1.5 cursor-pointer shadow-md transition-colors" title="Cambiar foto de perfil" data-testid="linkpage-upload-photo-btn">
                  {uploadingPhoto ? (
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Upload className="w-3 h-3" />
                  )}
                  <input type="file" accept="image/*" className="hidden" disabled={uploadingPhoto} onChange={uploadProfilePhoto} data-testid="linkpage-profile-photo-input" />
                </label>
              </div>
              <div>
                <p className="font-semibold text-gray-900">{user?.full_name}</p>
                <p className="text-sm text-gray-500">@{user?.username}</p>
                <p className="text-xs text-violet-600">{user?.user_type === 'author' ? 'Autor Verificado' : 'Audio-Lector'}</p>
                <p className="text-[11px] text-gray-400 mt-1">Toca el botón para subir tu foto (se aloja en ImgTool)</p>
              </div>
            </div>
            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600">
                <span className="font-medium text-violet-600">Importar:</span> Traer datos desde tu Perfil aquí
              </p>
              <p className="text-xs text-gray-600 mt-1">
                <span className="font-medium text-emerald-600">Exportar:</span> Actualizar tu Perfil con los datos de aquí
              </p>
            </div>
          </div>

          {/* Bio/Sinopsis */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Bio / Sinopsis</h3>
            <textarea
              value={formData.link_page_bio}
              onChange={(e) => setFormData(prev => ({ ...prev, link_page_bio: e.target.value }))}
              placeholder="Escribe una breve descripción sobre ti o tu trabajo..."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-violet-500/50"
              rows={3}
              maxLength={300}
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{formData.link_page_bio?.length || 0}/300</p>
          </div>

          {/* Fondo personalizado */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Palette className="w-5 h-5 text-violet-600" />
              Fondo
            </h3>
            
            {/* Tipo de fondo */}
            <div className="flex gap-2 mb-4">
              {[
                { id: 'gradient', label: 'Degradado' },
                { id: 'solid', label: 'Color sólido' },
                { id: 'image', label: 'Imagen' }
              ].map(type => (
                <button
                  key={type.id}
                  onClick={() => setBgType(type.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    bgType === type.id ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>

            {/* Degradados */}
            {bgType === 'gradient' && (
              <div className="grid grid-cols-5 gap-2">
                {GRADIENT_PRESETS.map(gradient => (
                  <button
                    key={gradient.name}
                    onClick={() => setFormData(prev => ({ ...prev, link_page_bg_color: gradient.value, link_page_wallpaper: '' }))}
                    className={`h-12 rounded-lg transition-transform hover:scale-105 ${
                      formData.link_page_bg_color === gradient.value ? 'ring-2 ring-offset-2 ring-violet-500' : ''
                    }`}
                    style={{ background: gradient.value }}
                    title={gradient.name}
                  />
                ))}
              </div>
            )}

            {/* Colores sólidos */}
            {bgType === 'solid' && (
              <div className="flex gap-2 flex-wrap">
                {COLOR_PRESETS.map(color => (
                  <button
                    key={color}
                    onClick={() => setFormData(prev => ({ ...prev, link_page_bg_color: color, link_page_wallpaper: '' }))}
                    className={`w-10 h-10 rounded-full transition-transform hover:scale-110 ${
                      formData.link_page_bg_color === color ? 'ring-2 ring-offset-2 ring-violet-500' : ''
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
                <input
                  type="color"
                  value={formData.link_page_bg_color?.startsWith('#') ? formData.link_page_bg_color : '#7c3aed'}
                  onChange={(e) => setFormData(prev => ({ ...prev, link_page_bg_color: e.target.value, link_page_wallpaper: '' }))}
                  className="w-10 h-10 rounded-full cursor-pointer"
                />
              </div>
            )}

            {/* Imagen de fondo */}
            {bgType === 'image' && (
              <div className="space-y-3">
                {formData.link_page_wallpaper ? (
                  <div className="relative">
                    <img src={formData.link_page_wallpaper} alt="Wallpaper" className="w-full h-32 object-cover rounded-xl" style={{ filter: `blur(${(formData.link_page_wallpaper_blur ?? 12) / 3}px)`, transform: 'scale(1.05)' }} />
                    <button
                      onClick={() => setFormData(prev => ({ ...prev, link_page_wallpaper: '' }))}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-violet-500 transition-colors">
                    {uploadingField === 'link_page_wallpaper' ? (
                      <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin mb-2" />
                    ) : (
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    )}
                    <span className="text-sm text-gray-500">Subir imagen de fondo (ImgTool)</span>
                    <span className="text-xs text-gray-400">Máx. 8MB</span>
                    <input type="file" accept="image/*" className="hidden" disabled={uploadingField === 'link_page_wallpaper'} onChange={(e) => uploadImageToImgTool(e, 'link_page_wallpaper')} data-testid="wallpaper-upload-input" />
                  </label>
                )}
                <button
                  type="button"
                  onClick={() => setGalleryPickerField('link_page_wallpaper')}
                  className="w-full flex items-center justify-center gap-2 text-sm text-violet-600 hover:text-violet-700 border border-violet-200 hover:border-violet-400 rounded-lg py-2 transition-colors"
                  data-testid="wallpaper-gallery-btn"
                >
                  <Image className="w-4 h-4" /> Elegir de mi galería
                </button>
                {/* URL externa */}
                <div>
                  <label className="text-xs font-medium text-gray-600 flex items-center gap-1 mb-1">
                    <Globe className="w-3 h-3" /> O pega una URL de imagen externa
                  </label>
                  <input
                    type="url"
                    value={formData.link_page_wallpaper?.startsWith('http') ? formData.link_page_wallpaper : ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, link_page_wallpaper: e.target.value }))}
                    placeholder="https://..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    data-testid="wallpaper-url-input"
                  />
                </div>
                {/* Blur slider */}
                {formData.link_page_wallpaper && (
                  <div>
                    <label className="text-xs font-medium text-gray-600 flex items-center justify-between mb-1">
                      <span>Efecto Blur (desenfoque)</span>
                      <span className="text-violet-600">{formData.link_page_wallpaper_blur ?? 12}px</span>
                    </label>
                    <input
                      type="range" min="0" max="30" step="1"
                      value={formData.link_page_wallpaper_blur ?? 12}
                      onChange={(e) => setFormData(prev => ({ ...prev, link_page_wallpaper_blur: parseInt(e.target.value) }))}
                      className="w-full accent-violet-600"
                      data-testid="wallpaper-blur-slider"
                    />
                    <p className="text-[11px] text-gray-400 mt-1">La imagen se amplía y desenfoca para un efecto wow tipo "frosted glass".</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Portada de libro */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Portada de Libro/Audiolibro</h3>
            <div className="flex items-center gap-4">
              {formData.link_page_book_cover ? (
                <div className="relative">
                  <img src={formData.link_page_book_cover} alt="Portada" className="w-20 h-28 object-cover rounded-lg shadow-md" />
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, link_page_book_cover: '' }))}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <label className="w-20 h-28 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-violet-500 transition-colors">
                  {uploadingField === 'link_page_book_cover' ? (
                    <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <BookOpen className="w-6 h-6 text-gray-400" />
                  )}
                  <span className="text-xs text-gray-400 mt-1">Subir</span>
                  <input type="file" accept="image/*" className="hidden" disabled={uploadingField === 'link_page_book_cover'} onChange={(e) => uploadImageToImgTool(e, 'link_page_book_cover')} data-testid="book-cover-upload-input" />
                </label>
              )}
              <div className="flex-1">
                <p className="text-sm text-gray-500">Aparecerá junto a tu foto de perfil en tu página de enlaces.</p>
                <button
                  type="button"
                  onClick={() => setGalleryPickerField('link_page_book_cover')}
                  className="mt-2 inline-flex items-center gap-2 text-sm text-violet-600 hover:text-violet-700 border border-violet-200 hover:border-violet-400 rounded-lg px-3 py-1.5 transition-colors"
                  data-testid="cover-gallery-btn"
                >
                  <Image className="w-4 h-4" /> Elegir de mi galería
                </button>
              </div>
            </div>
          </div>

          <GalleryPicker
            token={token}
            open={!!galleryPickerField}
            onClose={() => setGalleryPickerField(null)}
            onSelect={(url) => setFormData(prev => ({ ...prev, [galleryPickerField]: url }))}
          />

          {/* Enlaces personalizados */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Link2 className="w-5 h-5 text-violet-600" />
                Mis Enlaces ({formData.link_page_links.length}/15)
              </h3>
              <Button onClick={addLink} size="sm" disabled={formData.link_page_links.length >= 15}>
                <Plus className="w-4 h-4 mr-1" /> Añadir
              </Button>
            </div>

            {formData.link_page_links.length === 0 ? (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-xl">
                <Link2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="font-medium">No tienes enlaces aún</p>
                <p className="text-sm">Añade enlaces a tus redes, tiendas, podcasts, etc.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.link_page_links.map((link, index) => (
                  <div key={link.id} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                    <div className="flex items-start gap-3">
                      <div className="flex flex-col gap-1 pt-2">
                        <button onClick={() => moveLink(index, -1)} disabled={index === 0} className="text-gray-400 hover:text-gray-600 disabled:opacity-30">
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button onClick={() => moveLink(index, 1)} disabled={index === formData.link_page_links.length - 1} className="text-gray-400 hover:text-gray-600 disabled:opacity-30">
                          <ChevronDown className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            value={link.title}
                            onChange={(e) => updateLink(index, 'title', e.target.value)}
                            placeholder="Título"
                            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                          />
                          <select
                            value={link.icon}
                            onChange={(e) => updateLink(index, 'icon', e.target.value)}
                            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                          >
                            {ICON_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <input
                          type="url"
                          value={link.url}
                          onChange={(e) => updateLink(index, 'url', e.target.value)}
                          placeholder="https://..."
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                        />
                        <label className="flex items-center gap-2 text-sm text-gray-600">
                          <input
                            type="checkbox"
                            checked={link.enabled}
                            onChange={(e) => updateLink(index, 'enabled', e.target.checked)}
                            className="rounded text-violet-600"
                          />
                          Visible
                        </label>
                      </div>
                      <button onClick={() => removeLink(index)} className="text-red-500 hover:text-red-700 p-2">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Redes Sociales */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Redes Sociales</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {Object.keys(formData.social_links).map(platform => (
                <div key={platform}>
                  <label className="text-xs text-gray-500 mb-1 block capitalize">{platform}</label>
                  <input
                    type="url"
                    value={formData.social_links[platform] || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      social_links: { ...prev.social_links, [platform]: e.target.value }
                    }))}
                    placeholder={`https://${platform}.com/...`}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Contacto */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Información de Contacto</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Sitio web</label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="https://tu-sitio.com"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Email público</label>
                  <input
                    type="email"
                    value={formData.email_public}
                    onChange={(e) => setFormData(prev => ({ ...prev, email_public: e.target.value }))}
                    placeholder="contacto@ejemplo.com"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                  />
                  <label className="flex items-center gap-2 mt-2 text-xs text-gray-600">
                    <input
                      type="checkbox"
                      checked={formData.show_email_public}
                      onChange={(e) => setFormData(prev => ({ ...prev, show_email_public: e.target.checked }))}
                      className="rounded text-violet-600"
                    />
                    Mostrar en página
                  </label>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Teléfono</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+52 55 1234 5678"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                  />
                  <label className="flex items-center gap-2 mt-2 text-xs text-gray-600">
                    <input
                      type="checkbox"
                      checked={formData.show_phone_public}
                      onChange={(e) => setFormData(prev => ({ ...prev, show_phone_public: e.target.checked }))}
                      className="rounded text-violet-600"
                    />
                    Mostrar en página
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* BOTÓN GUARDAR - AL FINAL */}
          <div className="sticky bottom-4 z-10">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white py-6 text-lg shadow-lg"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                  Guardando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-5 h-5" />
                  Guardar Cambios
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* VISTA PREVIA EN TIEMPO REAL */}
        <div className="lg:sticky lg:top-4 h-fit">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-violet-600" />
              Vista Previa en Tiempo Real
            </h3>
            
            {/* Preview Container */}
            <div className="rounded-xl overflow-hidden shadow-lg" style={{ maxHeight: '600px', overflowY: 'auto' }}>
              <div className="min-h-[500px] p-4 relative overflow-hidden" style={getBackgroundStyle()}>
                {bgType === 'image' && formData.link_page_wallpaper && (
                  <>
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        backgroundImage: `url(${formData.link_page_wallpaper})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: `blur(${formData.link_page_wallpaper_blur ?? 12}px)`,
                        transform: 'scale(1.2)'
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/40 to-black/60 pointer-events-none" />
                  </>
                )}
                <div className="relative z-10">
                {/* Banner */}
                {formData.link_page_banner && (
                  <div className="rounded-t-2xl overflow-hidden mb-[-40px] relative z-0">
                    <img src={formData.link_page_banner} alt="Banner" className="w-full h-20 object-cover" />
                  </div>
                )}
                
                {/* Profile Card Preview */}
                <div className={`bg-white/10 backdrop-blur-lg rounded-2xl p-4 ${formData.link_page_banner ? 'pt-12' : ''}`}>
                  {/* Avatar & Book Cover */}
                  <div className="flex justify-center items-end gap-3 mb-3">
                    {formData.link_page_book_cover && (
                      <div className="w-14 h-20 rounded-lg overflow-hidden shadow-lg transform -rotate-6">
                        <img src={formData.link_page_book_cover} alt="Libro" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="relative">
                      {(photoPreview || user?.profile_photo) ? (
                        <img src={photoPreview || user.profile_photo} alt="" className="w-20 h-20 rounded-full object-cover border-2 border-white/30" />
                      ) : (
                        <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center border-2 border-white/30">
                          <span className="text-2xl font-bold text-white">{user?.full_name?.charAt(0)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Name */}
                  <div className="text-center mb-3">
                    <h4 className="text-lg font-bold text-white">{user?.full_name}</h4>
                    <p className="text-white/60 text-xs">@{user?.username}</p>
                  </div>

                  {/* Bio */}
                  {formData.link_page_bio && (
                    <p className="text-white/80 text-xs text-center mb-4 leading-relaxed">
                      {formData.link_page_bio}
                    </p>
                  )}

                  {/* Social Links Preview */}
                  {Object.values(formData.social_links).some(v => v) && (
                    <div className="flex justify-center gap-2 mb-4">
                      {Object.entries(formData.social_links).map(([platform, url]) => {
                        if (!url) return null;
                        return (
                          <div key={platform} className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center">
                            <Globe className="w-4 h-4 text-white" />
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Links Preview */}
                  {formData.link_page_links.filter(l => l.enabled).length > 0 && (
                    <div className="space-y-2 mb-4">
                      {formData.link_page_links.filter(l => l.enabled).map((link, i) => (
                        <div key={i} className="bg-white/10 rounded-lg p-3 flex items-center gap-2">
                          <Globe className="w-4 h-4 text-white/60" />
                          <span className="text-white text-sm flex-1 truncate">{link.title || 'Sin título'}</span>
                          <ExternalLink className="w-3 h-3 text-white/40" />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Referral CTA - AL FINAL */}
                  <div className="bg-white text-gray-900 text-center py-3 rounded-lg text-sm font-semibold">
                    Únete a Viubux
                  </div>

                  {/* Contact Info */}
                  {(formData.show_email_public || formData.show_phone_public || formData.website) && (
                    <div className="flex justify-center gap-3 mt-3 pt-3 border-t border-white/10">
                      {formData.show_email_public && formData.email_public && <Mail className="w-4 h-4 text-white/60" />}
                      {formData.show_phone_public && formData.phone && <Phone className="w-4 h-4 text-white/60" />}
                      {formData.website && <Globe className="w-4 h-4 text-white/60" />}
                    </div>
                  )}
                </div>
                </div>
              </div>
            </div>
          </div>

          {/* QR Code */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 mt-4">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <QrCode className="w-5 h-5 text-violet-600" />
              Código QR
            </h3>
            <div className="flex justify-center mb-4" ref={qrRef}>
              <div className="bg-white p-3 rounded-xl shadow border border-gray-100">
                <QRCodeSVG 
                  value={profileUrl}
                  size={150}
                  level="H"
                  includeMargin={true}
                  fgColor="#7c3aed"
                  imageSettings={{
                    src: "/viubux_logo_web.png",
                    height: 30,
                    width: 30,
                    excavate: true,
                  }}
                />
              </div>
            </div>
            <Button onClick={downloadQR} className="w-full bg-gray-900 hover:bg-gray-800">
              <Download className="w-4 h-4 mr-2" />
              Descargar QR
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkPageEditor;
